import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSession } from '../../../hooks/useSession';
import { ShoppingBag, AlertCircle, CheckCircle, TrendingUp, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import '../Products.css';

const ProductCard = ({ product, index, silverRate }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSession();

  const handleBookNow = () => {
    if (!isAuthenticated) {
      toast.error('Please login to book products');
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
    navigate('/booking', { state: { product } });
  };

  const getStockStatus = () => {
    if (product.stock > 10) return { label: 'In Stock', className: '', icon: CheckCircle };
    if (product.stock > 0) return { label: 'Low Stock', className: 'low', icon: AlertCircle };
    return { label: 'Out of Stock', className: 'out', icon: AlertCircle };
  };

  const stockStatus = getStockStatus();
  const StockIcon = stockStatus.icon;

  // Determine if price is live
  const isLivePrice = product.isLive && product.category === 'silver';

  // Check if image is an emoji or a file path
  const isEmoji = product.image && typeof product.image === 'string' && 
    !product.image.startsWith('/') && 
    !product.image.startsWith('http') &&
    !product.image.startsWith('data:');

  // Get image source - use the path directly from constants for images in public folder
  const getImageSrc = () => {
    if (isEmoji) {
      return null; // Will render as emoji
    }
    return product.image; // Use the path directly (e.g., '/Product-1.png')
  };

  const imageSrc = getImageSrc();

  return (
    <motion.div
      className={`product-card ${isLivePrice ? 'live-price' : ''}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: index * 0.08,
        type: 'spring',
        stiffness: 300,
        damping: 30
      }}
      whileHover={{
        y: -8,
        transition: { type: 'spring', stiffness: 400 }
      }}
    >
      <div className="card-glow" />

      <div className="product-image">
        <motion.div
          className="product-image-wrapper"
          whileHover={{ scale: 1.05, rotate: 3 }}
          transition={{ type: 'spring', stiffness: 400 }}
        >
          {imageSrc ? (
            <div className="image-container">
              <img
                src={imageSrc}
                alt={product.name}
                className="product-img"
                onError={(e) => {
                  // Fallback if image fails to load
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent) {
                    const emojiSpan = document.createElement('span');
                    emojiSpan.className = 'product-emoji-fallback';
                    emojiSpan.textContent = product.category === 'silver' ? '🥈' : '🪙';
                    parent.appendChild(emojiSpan);
                  }
                }}
              />
            </div>
          ) : (
            <span className="product-emoji-fallback">
              {product.image || (product.category === 'silver' ? '🥈' : '🪙')}
            </span>
          )}
        </motion.div>

        {product.category === 'gold' && (
          <span className="product-badge gold">Gold</span>
        )}
        {product.category === 'silver' && (
          <span className="product-badge silver">Silver</span>
        )}
        {product.popular && (
          <span className="product-badge popular">🔥 Popular</span>
        )}
        {isLivePrice && (
          <span className="product-badge live">
            <Zap size={12} />
            Live
          </span>
        )}
      </div>

      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        <div className="product-details">
          <span>{product.weight}</span>
          <span className="dot" />
          <span>{product.purity}</span>
          {product.category === 'silver' && (
            <>
              <span className="dot" />
              <span>Fine Silver</span>
            </>
          )}
        </div>

        <div className="product-price-row">
          <div className="price-section">
            <span className={`product-price ${product.priceChange >= 0 ? 'price-up' : 'price-down'}`}>
              <span className="currency">₹</span>
              {product.price.toLocaleString()}
              {product.priceChange !== undefined && (
                <span className={`price-change-indicator ${product.priceChange >= 0 ? 'up' : 'down'}`}>
                  {product.priceChange >= 0 ? '▲' : '▼'} {Math.abs(product.priceChange).toFixed(2)}%
                </span>
              )}
            </span>
            <span className="product-labour">+ Labour</span>
          </div>
          <span className={`product-stock ${stockStatus.className}`}>
            <StockIcon size={14} className="inline" />
            {stockStatus.label}
          </span>
        </div>

        {/* Live Price Breakdown for Silver */}
        {isLivePrice && silverRate && (
          <div className="live-price-breakdown">
            <div className="breakdown-item">
              <span className="breakdown-label">Silver Base:</span>
              <span className="breakdown-value">
                ₹{product.silverPricePerGram?.toFixed(2)}/gm
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Premium:</span>
              <span className="breakdown-value premium">
                +₹{product.premiumPerGram}/gm
              </span>
            </div>
            <div className="breakdown-item total">
              <span className="breakdown-label">Total:</span>
              <span className="breakdown-value total-price">
                ₹{product.totalPricePerGram?.toFixed(2)}/gm
              </span>
            </div>
            <div className="breakdown-update">
              <TrendingUp size={12} className="update-icon" />
              Updated: {product.lastUpdated || 'Just now'}
            </div>
          </div>
        )}

        <motion.button
          onClick={handleBookNow}
          className="book-btn"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={product.stock === 0}
        >
          <ShoppingBag size={18} />
          {product.stock === 0 ? 'Out of Stock' : 'Book Now'}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ProductCard;